import { ColumnDef } from '@tanstack/react-table';
import { Eye, Clock, CheckCircle, XCircle } from 'lucide-react';

import { DataTable } from '@/components/common/DataTable';
import { Account } from '@/types';
import { formatDate } from '@/utils';
import { useState } from 'react';
import { useDebounce } from '@/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { KycRequest, KycStatus, useGetTrusteeKycAccountsQuery } from '@/store/api/kycApi';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface KycStatusBadgeProps {
  status: KycStatus;
}

export function KycStatusBadge({ status }: KycStatusBadgeProps) {
  if (status === 'Approved') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
        <CheckCircle className="h-3 w-3" />
        KYC Verified
      </span>
    );
  }

  if (status === 'Rejected') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200">
        <XCircle className="h-3 w-3" />
        KYC Rejected
      </span>
    );
  }

  // Submitted / Pending
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
      <Clock className="h-3 w-3" />
      KYC Pending
    </span>
  );
}

export function TrusteeKycRequest() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState<KycStatus>(KycStatus.Submitted);

  const { data, isLoading, isError } = useGetTrusteeKycAccountsQuery(
    {
      skip: pageIndex * pageSize,
      take: pageSize,
      search: debouncedSearch,
      status: status,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageIndex(0); // 👈 always reset to first page
  };
  const requests: KycRequest[] = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  const columns: ColumnDef<KycRequest>[] = [
    {
      header: 'Name',
      cell: ({ row }) => {
        const user = row.original.account;
        const fullName = `${user.firstname} ${user.lastname}`;

        return (
          <div className="flex items-center space-x-3">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium overflow-hidden
              ${
                user.profileImage
                  ? 'border border-primary bg-transparent'
                  : 'bg-primary text-primary-foreground'
              }`}
            >
              {user.profileImage ? (
                <img src={user.profileImage} className="h-full w-full object-cover rounded-full" />
              ) : (
                user.firstname.charAt(0).toUpperCase()
              )}
            </div>

            <div>
              <div className="font-medium">{fullName}</div>
              <div className="text-sm text-muted-foreground">{user.email}</div>
            </div>
          </div>
        );
      },
    },

    {
      header: 'Mobile No.',
      cell: ({ row }) => row.original.account.mobile,
    },

    {
      header: 'Type',
      cell: ({ row }) => row.original.account.type,
    },

    {
      header: 'KYC',
      cell: ({ row }) => <KycStatusBadge status={row.original.status} />,
    },
    {
      header: 'Request Status',
      cell: ({ row }) => <span className="capitalize">{row.original.status}</span>,
    },

    {
      header: 'View Details',
      cell: ({ row }) => (
        <div
          onClick={() => navigate(`/trustee-kyc-details/${row.original.account.id}`)}
          className="flex justify-center cursor-pointer"
        >
          <Eye className="h-4 w-4" />
        </div>
      ),
    },

    {
      header: 'Created',
      cell: ({ row }) => formatDate(new Date(row.original.account.createdAt)),
    },
  ];

  const handleDelete = (selectedUsers: Account[]) => {
    if (confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) {
      alert(`${selectedUsers.length} users deleted!`);
    }
  };
  if (isLoading) {
    return <div className="p-6">Loading users...</div>;
  }

  if (isError) {
    return <div className="p-6 text-red-500">Failed to load users</div>;
  }

  const handleStatusChange = (value: KycStatus) => {
    setStatus(value);
    setPageIndex(0);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">User Management</h2>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Select value={status} onValueChange={(v) => setStatus(v as KycStatus)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Submitted">Submitted</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        data={requests}
        columns={columns}
        pageSize={pageSize}
        pageIndex={pageIndex}
        pageCount={Math.ceil(totalCount / pageSize)}
        manualPagination
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;

          setPageIndex(next.pageIndex);
          setPageSize(next.pageSize);
        }}
        onSearchChange={handleSearchChange}
      />
    </div>
  );
}
